import { C2P_MASTERCARD_IDENTITY_TYPE } from '@/base/const/click2pay.const'
import { CookieClass } from '@/base/class/cookie.class'
import { COOKIE_C2P_MC } from '@/base/const/cookies.const'

export class MastercardClick2payClass {
  constructor() {
    this.recognized = null
    this.idTokens = null
    this.profiles = null
    this.consumerPresent = null
    this.lastUsedCardTimestamp = null
    this.identityValidation = null
    this.maskedCard = null
    this.srcCorrelationId = null
    this.checkoutResult = null
    this.sdk = null
  }

  init({
    initiatorId,
    dpaId,
    transactionId,
    transactionAmount,
    transactionCurrencyCode,
    locale,
    dpaPresentationName,
    dpaName,
  }) {
    this.sdk = window.SRCSDK_MASTERCARD
    if (!this.sdk) {
      return Promise.reject(new Error('No Mastercard SDK instance!'))
    }
    const params = {
      srciTransactionId: transactionId,
      srcInitiatorId: initiatorId,
      srciDpaId: dpaId,
      dpaTransactionOptions: {
        transactionAmount: {
          transactionAmount: parseFloat(transactionAmount).toFixed(2),
          transactionCurrencyCode,
        },
        customInputData: {
          'com.mastercard.dcfExperience': 'WITHIN_CHECKOUT',
        },
        dpaLocale: locale,
        isGuestCheckout: false,
        confirmPayment: false,
        consumerNationalIdentifierRequested: false,
        dpaAcceptedBillingCountries: [],
        dpaAcceptedShippingCountries: [],
        consumerEmailAddressRequested: true,
        consumerNameRequested: true,
        consumerPhoneNumberRequested: true,
        dpaShippingPreference: 'NONE',
        dpaBillingPreference: 'NONE',
        paymentOptions: {
          dpaDynamicDataTtlMinutes: 15,
          dynamicDataType: 'CARD_APPLICATION_CRYPTOGRAM_SHORT_FORM',
        },
      },
      dpaData: {
        dpaPresentationName,
        dpaName,
      },
    }
    return this.sdk
      .init(params)
      .then(() => {
        return Promise.resolve()
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  }

  isRecognized() {
    try {
      const data = {
        recognitionTokens: [],
      }
      const recognitionToken = CookieClass.getCookie(COOKIE_C2P_MC)
      if (recognitionToken && recognitionToken.length) {
        data.recognitionTokens = [recognitionToken]
      }
      return this.sdk
        .isRecognized(data)
        .then((response) => {
          this.recognized = response.recognized
          if (response.recognized) {
            this.idTokens = response.idTokens
          }
          return Promise.resolve()
        })
        .catch((error) => {
          return Promise.reject(error)
        })
    } catch (error) {
      return Promise.reject(error)
    }
  }

  getSrcProfile(idToken) {
    const params = {
      idTokens: [idToken],
    }
    return this.sdk
      .getSrcProfile(params)
      .then((response) => {
        this.profiles = response.profiles
        this.srcCorrelationId = response.srcCorrelationId
        return Promise.resolve()
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  }

  identityLookup(email) {
    try {
      const params = {
        consumerIdentity: {
          identityProvider: 'SRC',
          identityType: C2P_MASTERCARD_IDENTITY_TYPE.email,
          identityValue: email,
        },
      }
      return this.sdk
        .identityLookup(params)
        .then((response) => {
          this.consumerPresent = response.consumerPresent
          this.lastUsedCardTimestamp = response.lastUsedCardTimestamp ? response.lastUsedCardTimestamp : null
          return Promise.resolve()
        })
        .catch((error) => {
          return Promise.reject(error)
        })
    } catch (error) {
      return Promise.reject(error)
    }
  }

  initiateIdentityValidation(validationChannel = null) {
    try {
      const params = {}
      if (validationChannel) {
        params.requestedValidationChannelId =
          validationChannel === C2P_MASTERCARD_IDENTITY_TYPE.phone
            ? C2P_MASTERCARD_IDENTITY_TYPE.phone
            : C2P_MASTERCARD_IDENTITY_TYPE.email
      }
      return this.sdk
        .initiateIdentityValidation(params)
        .then((response) => {
          if (!response.maskedValidationChannel) {
            return Promise.reject(new Error('No supported validation channel!'))
          }
          this.identityValidation = response
          return Promise.resolve()
        })
        .catch((error) => {
          return Promise.reject(error)
        })
    } catch (error) {
      return Promise.reject(error)
    }
  }

  completeIdentityValidation(validationData, complianceSettings) {
    try {
      const params = {
        validationData,
        complianceSettings,
      }
      return this.sdk
        .completeIdentityValidation(params)
        .then((response) => {
          this.idTokens = [response.idToken]
          if (response.recognitionToken) {
            CookieClass.setCookie(COOKIE_C2P_MC, response.recognitionToken, 180)
          }
          return Promise.resolve()
        })
        .catch((error) => {
          return Promise.reject(error)
        })
    } catch (error) {
      return Promise.reject(error)
    }
  }

  checkout(data) {
    try {
      return this.sdk
        .checkout(data)
        .then((response) => {
          if (!response || !response.checkoutResponse) {
            return Promise.reject(new Error('C2P Mastercard checkout failed: No checkout response from SDK!'))
          }
          if (!response.checkoutResponse.maskedCard || !response.checkoutResponse.maskedCard.srcDigitalCardId) {
            return Promise.reject(new Error('C2P Mastercard checkout failed: Missing srcDigitalCardId in response!'))
          }
          if (!response.checkoutResponse.srcCorrelationId) {
            return Promise.reject(new Error('C2P Mastercard checkout failed: Missing srcCorrelationId in response!'))
          }
          this.checkoutResult = response
          if (response.recognitionToken) {
            CookieClass.setCookie(COOKIE_C2P_MC, response.recognitionToken, 180)
          }
          return Promise.resolve()
        })
        .catch((error) => {
          return Promise.reject(error)
        })
    } catch (error) {
      return Promise.reject(error)
    }
  }

  unbindAppInstance() {
    try {
      const data = {
        recognitionTokens: [],
      }
      const recognitionToken = CookieClass.getCookie(COOKIE_C2P_MC)
      if (recognitionToken && recognitionToken.length) {
        data.recognitionTokens = [recognitionToken]
      }
      return this.sdk
        .unbindAppInstance(data)
        .then(() => {
          CookieClass.removeCookie(COOKIE_C2P_MC)
          return Promise.resolve()
        })
        .catch((error) => {
          return Promise.reject(error)
        })
    } catch (error) {
      return Promise.reject(error)
    }
  }

  resetInstance() {
    this.recognized = null
    this.idTokens = null
    this.profiles = null
    this.consumerPresent = null
    this.lastUsedCardTimestamp = null
    this.identityValidation = null
    this.maskedCard = null
    this.srcCorrelationId = null
    this.checkoutResult = null
    return Promise.resolve()
  }
}

export const click2PayMastercardClass = new MastercardClick2payClass()
